import React from 'react';
import { motion } from 'framer-motion';
import { Users, Share2, TrendingUp, Heart } from 'lucide-react';

const Slide8Competition = () => {
    const growthLevers = [
        {
            title: "Communities",
            icon: Users,
            desc: "Tapping into existing financial communities & forums.",
            points: ["Niche FB/Reddit Groups", "Trading Communities", "Personal Finance Forums"],
            pos: "top-0 left-0 -translate-x-1/2 -translate-y-1/2"
        },
        {
            title: "B2B Partners",
            icon: TrendingUp,
            desc: "Strategic alliances with Fintech & Banking infra.",
            points: ["Bank API Partners", "Employee Wellness Programs", "SaaS Ecosystems"],
            pos: "top-0 right-0 translate-x-1/2 -translate-y-1/2"
        },
        {
            title: "Viral Outcomes",
            icon: Share2,
            desc: "Users sharing their financial wins & insights.",
            points: ["Insight Referrals", "Success Story Sharing", "Social Proof Mechanics"],
            pos: "bottom-0 right-0 translate-x-1/2 translate-y-1/2"
        },
        {
            title: "Campus Program",
            icon: Heart,
            desc: "Building trust with the next gen of wealth creators.",
            points: ["Ambassador Network", "Financial Literacy Workshops", "Early Adopter Perks"],
            pos: "bottom-0 left-0 -translate-x-1/2 translate-y-1/2"
        }
    ];

    return (
        <div className="w-full h-full bg-background p-8 lg:p-16 flex flex-col items-center justify-center relative overflow-hidden">
            {/* Background Grid Texture */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                style={{ backgroundImage: 'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)', backgroundSize: '40px 40px' }}
            />

            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-16 relative z-10"
            >
                <h2 className="text-4xl lg:text-6xl font-bold mb-4">Trust-Based, <span className="text-primary text-glow">Product-Led Growth</span></h2>
                <p className="text-xl text-muted-foreground">Metric: Low CAC via trust; Conversion via outcome improvement.</p>
            </motion.div>

            {/* Flywheel Visual */}
            <div className="relative w-[500px] h-[500px] lg:w-[800px] lg:h-[800px] flex items-center justify-center scale-75 lg:scale-100">

                {/* Center Hub */}
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-24 h-24 lg:w-32 lg:h-32 bg-primary rounded-full flex items-center justify-center z-20 shadow-[0_0_50px_rgba(0,255,0,0.4)]"
                >
                    <span className="text-xl lg:text-2xl font-bold text-black tracking-tight">TRUST</span>
                </motion.div>

                {growthLevers.map((lever, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2 + index * 0.1 }}
                        className={`absolute ${lever.pos} w-[220px] lg:w-[320px] z-30`}
                    >
                        <div className="bg-card border border-primary/20 p-5 lg:p-7 rounded-2xl shadow-2xl backdrop-blur-sm group hover:border-primary transition-all duration-300">
                            <div className="flex items-center gap-3 lg:gap-4 mb-4">
                                <div className="p-2 lg:p-3 bg-primary/10 rounded-xl group-hover:bg-primary/20 transition-colors">
                                    <lever.icon className="w-6 h-6 lg:w-8 lg:h-8 text-primary" />
                                </div>
                                <h3 className="text-base lg:text-2xl font-bold text-white tracking-tight">{lever.title}</h3>
                            </div>
                            <p className="text-xs lg:text-sm text-muted-foreground mb-4 leading-relaxed">
                                {lever.desc}
                            </p>
                            <ul className="space-y-2">
                                {lever.points.map((point, i) => (
                                    <li key={i} className="flex items-center gap-3 text-xs lg:text-base text-white/80">
                                        <div className="w-1.5 h-1.5 bg-primary rounded-full shrink-0" />
                                        {point}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </motion.div>
                ))}

                {/* Connecting Circle/Flywheel lines */}
                <div className="absolute inset-0 rounded-full border border-dashed border-primary/20 animate-spin-slow" />
                <div className="absolute inset-16 lg:inset-24 rounded-full border border-primary/10" />
            </div>
        </div>
    );
};

export default Slide8Competition;